<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CartAPI from 'cart/api/cart';
import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const { updateUISettings } = useUISettings();
const { formatDate: formatWithLocale, localeTag } = useDateLocale();

const carts = ref([]);
const loading = ref(true);
const error = ref('');

const fetchCarts = async () => {
  try {
    loading.value = true;
    const response = await CartAPI.getContactCarts(props.contactId);
    carts.value = response.data.carts;
  } catch (e) {
    error.value = e.response?.data?.error || 'Error loading carts';
  } finally {
    loading.value = false;
  }
};

watch(() => props.contactId, fetchCarts, { immediate: true });

const formatDate = dateString => formatWithLocale(dateString, 'd MMM yyyy');

const formatCurrency = (amount, currency) =>
  new Intl.NumberFormat(localeTag.value, {
    style: 'currency',
    currency: currency || 'BRL',
  }).format(amount);

const getDisplayStatus = cart => {
  if (cart.expires_at && new Date(cart.expires_at) < new Date())
    return 'expired';
  return cart.status;
};

const getStatusBadgeClass = status => {
  const classes = {
    open: 'bg-n-teal-3 text-n-teal-11',
    expired: 'bg-n-amber-3 text-n-amber-11',
    abandoned: 'bg-n-slate-3 text-n-slate-11',
    converted: 'bg-n-blue-3 text-n-blue-11',
  };
  return classes[status] || classes.abandoned;
};

const onCartClick = cart => {
  store.dispatch('cart/setCart', {
    conversationId: props.conversationId,
    cart,
  });
  updateUISettings({
    is_cart_panel_open: true,
    is_contact_sidebar_open: false,
  });
};
</script>

<template>
  <div
    v-if="loading"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else-if="error" class="px-6 py-10 text-sm text-center text-n-ruby-11">
    {{ error }}
  </div>
  <p
    v-else-if="!carts.length"
    class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11"
  >
    {{ t('CONTACTS_LAYOUT.SIDEBAR.CARTS.EMPTY_STATE') }}
  </p>
  <div v-else class="px-3 py-3 space-y-2">
    <div
      v-for="cart in carts"
      :key="cart.id"
      class="rounded-lg border border-n-weak overflow-hidden"
    >
      <button
        class="w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-n-alpha-1 transition-colors"
        @click="onCartClick(cart)"
      >
        <div class="flex flex-col gap-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-n-slate-12 truncate">
              #{{ cart.id }}
            </span>
            <span
              :class="getStatusBadgeClass(getDisplayStatus(cart))"
              class="text-[10px] px-1.5 py-0.5 rounded shrink-0"
            >
              {{ t(`CONVERSATION.CART.STATUS.${getDisplayStatus(cart)}`) }}
            </span>
          </div>
          <div class="text-xs text-n-slate-11 flex items-center gap-2">
            <span>{{ formatDate(cart.created_at) }}</span>
            <span class="text-n-slate-10">&middot;</span>
            <span class="text-n-slate-11">
              {{
                t(
                  'CONTACTS_LAYOUT.SIDEBAR.CARTS.ITEMS_COUNT',
                  { count: cart.items_count },
                  cart.items_count
                )
              }}
            </span>
            <template v-if="cart.total_amount">
              <span class="text-n-slate-10">&middot;</span>
              <span class="font-medium text-n-slate-12">
                {{ formatCurrency(cart.total_amount, cart.currency) }}
              </span>
            </template>
          </div>
        </div>
        <i class="i-lucide-chevron-right text-n-slate-10 text-base shrink-0" />
      </button>
    </div>
  </div>
</template>
